import { Injectable } from '@angular/core';
import { Plugin, registerPlugin } from '@capacitor/core';
import { Store } from '@ngxs/store';
import { HardwareDeviceSelectors } from 'src/app/store/hardware-device/hardware-device.selector';

export interface HttpPluginResponse {
  status: number;
  statusText: string;
  data: string;
  headers: { [key: string]: string };
  contentType: string;
  isXml: boolean;
  isJson: boolean;
}

export interface HttpPluginRequest {
  url: string;
  data?: string;
  headers?: { [key: string]: string };
  contentType?: string;
}

export interface HttpPlugin extends Plugin {
  get(options: {
    url: string;
    headers?: { [key: string]: string };
  }): Promise<HttpPluginResponse>;
  post(options: HttpPluginRequest): Promise<HttpPluginResponse>;
  put(options: HttpPluginRequest): Promise<HttpPluginResponse>;
  delete(options: {
    url: string;
    headers?: { [key: string]: string };
  }): Promise<HttpPluginResponse>;
}

// interface NFCDataI {
//   data: string;
// }

@Injectable({
  providedIn: 'root'
})
export class HttpPluginService {
  private httpPlugin: HttpPlugin;

  constructor(private readonly _store: Store) {
    this.httpPlugin = registerPlugin<HttpPlugin>('HttpPlugin');
  }

  private get _isAndroid() {
    return this._store.selectSnapshot(HardwareDeviceSelectors.isAndroid);
  }

  private get _isIOS() {
    return this._store.selectSnapshot(HardwareDeviceSelectors.isIOS);
  }

  private get _isMobile() {
    return this._isAndroid || this._isIOS;
  }

  async get(
    url: string,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    if (!this._isMobile) {
      return null;
    }

    try {
      return await this.httpPlugin.get({ url, headers });
    } catch (error) {
      throw error;
    }
  }

  async post(
    url: string,
    data?: string,
    headers?: { [key: string]: string },
    contentType?: string
  ): Promise<HttpPluginResponse | null> {
    if (!this._isMobile) {
      return null;
    }

    try {
      return await this.httpPlugin.post({
        url,
        data,
        headers,
        contentType: contentType || 'application/json'
      });
    } catch (error) {
      throw error;
    }
  }

  async put(
    url: string,
    data?: string,
    headers?: { [key: string]: string },
    contentType?: string
  ): Promise<HttpPluginResponse | null> {
    if (!this._isMobile) {
      return null;
    }

    try {
      return await this.httpPlugin.put({
        url,
        data,
        headers,
        contentType: contentType || 'application/json'
      });
    } catch (error) {
      throw error;
    }
  }

  async delete(
    url: string,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    if (!this._isMobile) {
      return null;
    }

    try {
      return await this.httpPlugin.delete({ url, headers });
    } catch (error) {
      throw error;
    }
  }

  // Convenience method for XML requests
  async postXml(
    url: string,
    xmlData: string,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    return this.post(url, xmlData, headers, 'application/xml');
  }

  async putXml(
    url: string,
    xmlData: string,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    return this.put(url, xmlData, headers, 'application/xml');
  }

  // Convenience method for JSON requests
  async postJson(
    url: string,
    jsonData: any,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    const data =
      typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
    return this.post(url, data, headers, 'application/json');
  }

  async putJson(
    url: string,
    jsonData: any,
    headers?: { [key: string]: string }
  ): Promise<HttpPluginResponse | null> {
    const data =
      typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
    return this.put(url, data, headers, 'application/json');
  }
}
